import { createFileRoute } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Image, MapPin, VideoCamera } from '@phosphor-icons/react';
import { agencyProfileSchema, type AgencyProfile, type AgencyProfileInput } from '@mybusinessmate/schema';
import { useState } from 'react';
import {
  FieldGrid,
  LastChanged,
  RowValue,
  SettingCard,
  SettingRow,
} from '../../../../../../components/settings/setting-card';
import { UnsavedBar } from '../../../../../../components/settings/unsaved-bar';
import { ValueEditorDialog } from '../../../../../../components/settings/value-editor-dialog';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { Select } from '../../../../../../components/ui/select';
import { TextArea } from '../../../../../../components/ui/text-area';
import { Toggle } from '../../../../../../components/ui/toggle';
import { useToast } from '../../../../../../components/ui/toast';
import { formatChangedDate } from '../../../../../../lib/format-edited';
import { GST_STATES } from '../../../../../../lib/gst-states';
import { useWorkspaceSection } from '../../../../../../lib/workspace-api';
import { Skeleton } from '../../../../../../components/ui/skeleton';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/agency')({
  component: AgencyPage,
});

function AgencyPage() {
  const section = useWorkspaceSection('agency');
  if (!section.data) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <AgencyForm stored={section.data} onSave={(form) => section.save.mutateAsync(form)} />;
}

function AgencyForm({
  stored,
  onSave: persist,
}: {
  stored: AgencyProfile & { changed?: { by: string; at: string } };
  onSave: (form: AgencyProfileInput) => Promise<unknown>;
}) {
  const toast = useToast();
  const [venueOpen, setVenueOpen] = useState(false);

  const values: AgencyProfileInput = {
    legalName: stored.legalName,
    brandName: stored.brandName,
    gstin: stored.gstin,
    gstState: stored.gstState,
    address: stored.address,
    supportEmail: stored.supportEmail,
    supportPhone: stored.supportPhone,
    defaultVenue: stored.defaultVenue,
    meetLinkForInPerson: stored.meetLinkForInPerson,
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<AgencyProfileInput, unknown, AgencyProfile>({
    resolver: zodResolver(agencyProfileSchema),
    mode: 'onChange',
    values,
  });

  const onSave = handleSubmit(async (form) => {
    try {
      await persist(form);
    } catch {
      return; // the data layer already told the person why
    }
    reset(form);
    toast({ title: 'Agency profile saved' });
  });

  const invalid = Object.keys(errors).length;
  const venue = watch('defaultVenue') ?? '';

  return (
    <>
      <SettingCard title="Business details" subtitle="Shown on GST invoices, emails and the client portal footer.">
        <FieldGrid>
          <Input variant="filled" label="Legal name" error={errors.legalName?.message} {...register('legalName')} />
          <Input variant="filled" label="Brand name" error={errors.brandName?.message} {...register('brandName')} />
          <Input
            variant="filled"
            label="GSTIN"
            autoCapitalize="characters"
            spellCheck={false}
            error={errors.gstin?.message}
            {...register('gstin')}
          />
          <Select variant="filled" label="State (for GST)" error={errors.gstState?.message} {...register('gstState')}>
            <option value="">Pick the state</option>
            {GST_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </FieldGrid>
        <TextArea
          variant="filled"
          label="Registered address"
          rows={3}
          containerClassName="pt-(--mbm-space-4)"
          error={errors.address?.message}
          {...register('address')}
        />
        <FieldGrid className="pt-(--mbm-space-4)">
          <Input
            variant="filled"
            label="Support email"
            type="email"
            error={errors.supportEmail?.message}
            {...register('supportEmail')}
          />
          <Input
            variant="filled"
            label="Support phone"
            type="tel"
            error={errors.supportPhone?.message}
            {...register('supportPhone')}
          />
        </FieldGrid>
      </SettingCard>

      <SettingCard
        title="Logo"
        subtitle="Used on invoices, emails and the sign-in page. SVG or PNG, at least 512 px wide."
      >
        {/* TODO(api): the presigned-upload endpoint has a client `logo` category only; an agency logo needs its own. */}
        <SettingRow
          icon={Image}
          title="No logo yet"
          subtitle="Add one so invoices and emails carry your brand."
          trailing={
            <Button
              variant="secondary"
              size="row"
              onClick={() => toast({ title: 'Logo upload is coming soon', tone: 'info' })}
            >
              Upload
            </Button>
          }
        />
      </SettingCard>

      <SettingCard title="Meetings" subtitle="Defaults used when you approve a client meeting.">
        <SettingRow
          icon={MapPin}
          title="Default office venue"
          subtitle="Used for in-person meetings unless you pick another"
          trailing={<RowValue>{venue || 'Not set'}</RowValue>}
          onClick={() => setVenueOpen(true)}
        />
        <SettingRow
          icon={VideoCamera}
          title="Add a Google Meet link to in-person meetings"
          subtitle="So clients can join online if they can’t make it"
          trailing={
            <Toggle
              aria-label="Add a Google Meet link to in-person meetings"
              checked={watch('meetLinkForInPerson') ?? true}
              onCheckedChange={(on) => setValue('meetLinkForInPerson', on, { shouldDirty: true })}
            />
          }
        />
      </SettingCard>

      <LastChanged by={stored.changed?.by} date={stored.changed ? formatChangedDate(stored.changed.at) : undefined} />

      <ValueEditorDialog
        open={venueOpen}
        onClose={() => setVenueOpen(false)}
        title="Default office venue"
        label="Venue"
        editor={{ kind: 'text', value: venue, placeholder: 'Moguls Court, Basheerbagh', maxLength: 120 }}
        onSave={(v) => setValue('defaultVenue', String(v), { shouldDirty: true })}
      />
      <UnsavedBar
        visible={isDirty}
        message={
          invalid > 0
            ? `${invalid} ${invalid === 1 ? 'field needs' : 'fields need'} fixing before you can save`
            : undefined
        }
        saveDisabled={invalid > 0}
        saving={isSubmitting}
        onDiscard={() => reset(values)}
        onSave={() => void onSave()}
      />
    </>
  );
}
